import { debug } from './debug';

// Map Intl short weekday names to ISO 8601 numbers
const ISO_DOW = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 };

/**
 * ISO 8601 day of week (1 = Monday … 7 = Sunday) of a date, optionally in a
 * specific IANA timezone (e.g. the merchant's). Without a timezone — or if
 * the timezone is invalid — the browser's local day is used.
 *
 * Keep in sync with isoDayOfWeek in api/_utils/discountValidity.js.
 */
export function isoDayOfWeek(date, timezone) {
  if (timezone) {
    try {
      const weekday = new Intl.DateTimeFormat('en-US', { timeZone: timezone, weekday: 'short' }).format(date);
      if (ISO_DOW[weekday]) return ISO_DOW[weekday];
    } catch (e) {
      debug.log('Invalid timezone for isoDayOfWeek, using browser-local day:', timezone);
    }
  }
  const day = date.getDay();
  return day === 0 ? 7 : day;
}

/**
 * Calendar date ('YYYY-MM-DD') of a date, optionally in a specific IANA
 * timezone (e.g. the merchant's). Without a timezone — or if the timezone is
 * invalid — the browser's local calendar date is used. Comparable
 * lexicographically against DATE columns ('YYYY-MM-DD' strings).
 *
 * Keep in sync with localDateString in api/_utils/discountValidity.js.
 */
export function localDateString(date, timezone) {
  const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
  try {
    // en-CA formats as YYYY-MM-DD; undefined timeZone = browser local
    return new Intl.DateTimeFormat('en-CA', { ...options, timeZone: timezone || undefined }).format(date);
  } catch (e) {
    debug.log('Invalid timezone for localDateString, using browser-local date:', timezone);
    return new Intl.DateTimeFormat('en-CA', options).format(date);
  }
}

/**
 * Seconds since midnight of a date's wall-clock time, optionally in a specific
 * IANA timezone (e.g. the merchant's). Without a timezone — or if the timezone
 * is invalid — the browser's local time is used.
 */
function secondsOfDay(date, timezone) {
  const options = { hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' };
  let parts;
  try {
    parts = new Intl.DateTimeFormat('en-GB', { ...options, timeZone: timezone || undefined }).formatToParts(date);
  } catch (e) {
    debug.log('Invalid timezone for secondsOfDay, using browser-local time:', timezone);
    parts = new Intl.DateTimeFormat('en-GB', options).formatToParts(date);
  }
  const part = (type) => Number(parts.find(p => p.type === type)?.value || 0);
  return part('hour') * 3600 + part('minute') * 60 + part('second');
}

// 'HH:MM[:SS]' (a TIME column) -> seconds since midnight.
function timeToSeconds(time) {
  const [h, m, s] = String(time).split(':').map(Number);
  return (h || 0) * 3600 + (m || 0) * 60 + (s || 0);
}

/**
 * Check if a pricing rule is valid for a given reference date.
 *
 * Validates against:
 * - valid_from: rule not yet active (date comparison)
 * - valid_to: rule expired (date comparison)
 * - valid_days_of_week: day-of-week restriction — ISO 8601 numbering
 *   (1=Monday … 7=Sunday, unified across all readers in migration 137)
 * - valid_time_from / valid_time_until: daily window on the rental START's
 *   wall-clock time, inclusive at both ends — exactly as calculate_rental_price
 *   applies it (migration 138), with no overnight wrap-around because the RPC
 *   has none, and the manage-booking app prices through the RPC.
 *
 * Date, day-of-week and time-of-day boundaries are evaluated in the merchant's
 * timezone when one is provided, matching calculate_rental_price (migration
 * 138) and api/create-booking.js.
 *
 * Rules with NULL/undefined/empty values for these fields are treated as "applies always".
 *
 * @param {Object} rule - A pricing rule object from the database
 * @param {Date} referenceDate - The rental start date to validate against
 * @param {string} [timezone] - Optional merchant IANA timezone for date and
 *   day-of-week boundaries; defaults to the browser's local timezone
 * @param {Object} [options]
 * @param {boolean} [options.checkTimeWindow=true] - Pass false when
 *   referenceDate is a calendar date with no time chosen yet, so a rule's
 *   daily window is not judged against midnight.
 * @returns {boolean} Whether the rule is valid for the given date
 */
export function isPricingRuleValid(rule, referenceDate, timezone, options = {}) {
  if (!referenceDate) return true;

  const refDate = referenceDate instanceof Date ? referenceDate : new Date(referenceDate);

  // valid_from/valid_to are DATE columns ('YYYY-MM-DD' strings, no time
  // component); compare against the reference date's calendar date so
  // time-of-day never leaks into the comparison.
  const refDateStr = localDateString(refDate, timezone);

  // valid_from: rule not yet active
  if (rule.valid_from && refDateStr < String(rule.valid_from).slice(0, 10)) return false;

  // valid_to: rule expired
  if (rule.valid_to && refDateStr > String(rule.valid_to).slice(0, 10)) return false;

  // valid_days_of_week: ISO 8601 numbering (1=Monday … 7=Sunday)
  // null or empty array means applies to all days
  if (rule.valid_days_of_week && rule.valid_days_of_week.length > 0) {
    const dayOfWeek = isoDayOfWeek(refDate, timezone);
    if (!rule.valid_days_of_week.includes(dayOfWeek)) return false;
  }

  // valid_time_from / valid_time_until: daily window, inclusive (see above)
  const { checkTimeWindow = true } = options;
  if (checkTimeWindow && (rule.valid_time_from || rule.valid_time_until)) {
    const startSeconds = secondsOfDay(refDate, timezone);
    if (rule.valid_time_from && startSeconds < timeToSeconds(rule.valid_time_from)) return false;
    if (rule.valid_time_until && startSeconds > timeToSeconds(rule.valid_time_until)) return false;
  }

  return true;
}

/**
 * Filter an array of pricing rules to only include those valid for a given date and flow type.
 * Combines the existing active check with the new validity checks.
 *
 * @param {Array} pricingRules - Array of pricing rule objects
 * @param {Date} referenceDate - The rental start date to validate against
 * @param {Object} [options] - Optional filtering options
 * @param {string} [options.flowType] - Booking flow type: 'immediate' or 'reservation'. If omitted, no flow filtering is applied.
 * @param {string} [options.timezone] - Optional merchant IANA timezone for the day-of-week boundary; defaults to the browser's local timezone.
 * @param {boolean} [options.checkTimeWindow=true] - See isPricingRuleValid.
 * @returns {Array} Filtered pricing rules that are active and valid
 */
export function getValidPricingRules(pricingRules, referenceDate, options = {}) {
  if (!pricingRules || pricingRules.length === 0) return [];

  const { flowType, timezone, checkTimeWindow = true } = options;

  const result = pricingRules.filter(pr => {
    const isActive = pr.active !== false;
    const isValid = isPricingRuleValid(pr, referenceDate, timezone, { checkTimeWindow });
    // Safety net: exclude rules not available on the 'online' channel
    const isOnlineChannel = !pr.channels || pr.channels.includes('online');
    // Flow type filter: NULL/undefined flow_type on the rule means "both flows"
    const isFlowMatch = !pr.flow_type || !flowType || pr.flow_type === flowType;
    if (!isActive || !isValid || !isOnlineChannel || !isFlowMatch) {
      debug.log(`🚫 [getValidPricingRules] Filtered out rule ${pr.id}: active=${isActive}, valid=${isValid}, onlineChannel=${isOnlineChannel}, flowMatch=${isFlowMatch}`, {
        price: pr.price, duration_type: pr.duration_type, duration_value: pr.duration_value,
        valid_from: pr.valid_from, valid_to: pr.valid_to, valid_days_of_week: pr.valid_days_of_week,
        valid_time_from: pr.valid_time_from, valid_time_until: pr.valid_time_until,
        channels: pr.channels, flow_type: pr.flow_type, requestedFlowType: flowType,
        referenceDate: referenceDate?.toString?.() || referenceDate
      });
    }
    return isActive && isValid && isOnlineChannel && isFlowMatch;
  });

  debug.log(`✅ [getValidPricingRules] ${result.length}/${pricingRules.length} rules passed validation (flowType=${flowType || 'any'})`);
  return result;
}

// A rule restricted to particular locations, as opposed to one for all of them
// (location_ids NULL, not an array, or empty — see migration 038).
function isLocationSpecific(rule) {
  return Array.isArray(rule?.location_ids) && rule.location_ids.length > 0;
}

/**
 * Does a rule apply at this location? location_ids NULL, not an array, or
 * EMPTY means every location — the column's documented meaning (migration 038)
 * and what calculate_rental_price does. An empty array used to be treated as
 * "no location" by the add-on fetch.
 */
export function pricingRuleAppliesToLocation(rule, locationId) {
  if (!isLocationSpecific(rule)) return true;
  return rule.location_ids.map(String).includes(String(locationId));
}

/**
 * The order in which pricing rules are considered whenever one has to be chosen
 * among several: the order calculate_rental_price returns them in (migration
 * 138) — location-specific rules first, then display_order, then price — plus
 * id, so that the order is total. A missing display_order sorts last, as NULL
 * does in the RPC. The manage-booking app uses the same order.
 */
export function comparePricingRules(a, b) {
  const locA = isLocationSpecific(a) ? 0 : 1;
  const locB = isLocationSpecific(b) ? 0 : 1;
  if (locA !== locB) return locA - locB;

  const orderA = a.display_order == null ? Infinity : Number(a.display_order);
  const orderB = b.display_order == null ? Infinity : Number(b.display_order);
  if (orderA !== orderB) return orderA < orderB ? -1 : 1;

  const priceA = parseFloat(a.price);
  const priceB = parseFloat(b.price);
  if (priceA !== priceB) return (isNaN(priceA) ? Infinity : priceA) < (isNaN(priceB) ? Infinity : priceB) ? -1 : 1;

  const idA = String(a.id ?? '');
  const idB = String(b.id ?? '');
  return idA < idB ? -1 : idA > idB ? 1 : 0;
}

/**
 * Choose among rules that all match the requested duration. Variants of ONE
 * rung (same duration_type and duration_value — size, location, flow or season
 * variants) are decided by comparePricingRules; between DIFFERENT rungs that
 * match equally (calendar_day N vs multi_day N for "Nd", week N vs multi_day
 * 7N for "Nw"), the cheapest wins, as agreed with the manage-booking app.
 *
 * @param {Array} matches - rules already in comparePricingRules order
 */
function pickCheapestRung(matches) {
  const firstPerRung = new Map();
  for (const rule of matches) {
    const rung = `${rule.duration_type}|${parseFloat(rule.duration_value)}`;
    if (!firstPerRung.has(rung)) firstPerRung.set(rung, rule);
  }
  let best = null;
  for (const rule of firstPerRung.values()) {
    if (!best || parseFloat(rule.price) < parseFloat(best.price)) best = rule;
  }
  return best;
}

/**
 * Match a duration ID (e.g., "4h", "1d", "1w") to the best pricing rule.
 *
 * @param {Array} pricingRules - Array of pricing rule objects
 * @param {string} durationId - Duration identifier (e.g., "4h", "1d", "1w")
 * @param {Object} [options]
 * @param {boolean} [options.cheapestAmongEqual=false] - Consider rules in
 *   comparePricingRules order, and among exact matches take the cheapest rung
 *   (pickCheapestRung). Off, the rules' own order decides and the first exact
 *   match wins — products still rely on that until they move to the same rule.
 * @returns {Object|null} The matching pricing rule, or null if no rules available
 */
export function findMatchingPricingRule(pricingRules, durationId, options = {}) {
  if (!pricingRules || pricingRules.length === 0) {
    return null;
  }

  const { cheapestAmongEqual = false } = options;
  const rules = cheapestAmongEqual ? [...pricingRules].sort(comparePricingRules) : pricingRules;
  const exactMatch = (predicate) => cheapestAmongEqual
    ? pickCheapestRung(rules.filter(predicate))
    : rules.find(predicate);

  // Parse duration ID (e.g., "4h", "1d", "1w")
  const match = durationId?.match(/^(\d+)([hdw])$/);
  if (!match) {
    return rules.find(p => p.featured) || rules[0];
  }

  const [, value, unit] = match;
  const durationValue = parseInt(value);

  // Convert duration to comparable format
  let targetDurationInDays;
  if (unit === 'h') {
    const matchingRule = exactMatch(r =>
      r.duration_type === 'hours' && parseFloat(r.duration_value) === durationValue
    );
    if (matchingRule) return matchingRule;
  } else if (unit === 'd') {
    const matchingRule = exactMatch(r =>
      (r.duration_type === 'calendar_day' || r.duration_type === 'multi_day') &&
      parseFloat(r.duration_value) === durationValue
    );
    if (matchingRule) return matchingRule;
    targetDurationInDays = durationValue;
  } else if (unit === 'w') {
    const daysInWeeks = durationValue * 7;
    const matchingRule = exactMatch(r => {
      if (r.duration_type === 'week') {
        return parseFloat(r.duration_value) === durationValue;
      }
      if (r.duration_type === 'multi_day') {
        return parseFloat(r.duration_value) === daysInWeeks;
      }
      return false;
    });
    if (matchingRule) return matchingRule;
    targetDurationInDays = daysInWeeks;
  }

  // If no exact match, find closest pricing rule (for multi-day/week)
  if (targetDurationInDays) {
    const multiDayRules = rules.filter(r =>
      r.duration_type === 'multi_day' || r.duration_type === 'calendar_day' || r.duration_type === 'week'
    );
    const closestRule = multiDayRules.reduce((closest, rule) => {
      const ruleDays = rule.duration_type === 'week'
        ? parseFloat(rule.duration_value) * 7
        : parseFloat(rule.duration_value);
      const closestDays = closest
        ? (closest.duration_type === 'week' ? parseFloat(closest.duration_value) * 7 : parseFloat(closest.duration_value))
        : Infinity;
      const diffCurrent = Math.abs(ruleDays - targetDurationInDays);
      const diffClosest = Math.abs(closestDays - targetDurationInDays);
      return diffCurrent < diffClosest ? rule : closest;
    }, null);
    if (closestRule) return closestRule;
  }

  // Fallback to featured or first rule
  return rules.find(p => p.featured) || rules[0];
}

/**
 * The rule an add-on is priced with on the booking page. ONE function for the
 * add-ons modal — whose choice becomes the booking item's unit_price and
 * pricing_rule_id — and for Checkout, whose total is what the customer is
 * charged. They used to match independently and could disagree whenever no
 * rule fitted the duration exactly: the modal fell back to the featured rule,
 * Checkout to the closest one, so the charge and the record differed.
 *
 * Checkout's semantics are kept, since they are what charges: exact match
 * (cheapest among equal rungs), else the closest multi-day rule, else the
 * featured rule, else the first — all in comparePricingRules order.
 *
 * @returns {Object|null} the rule, or null when the add-on has no valid rule
 *   (it is then priced at its base price)
 */
export function selectAddonPricingRule(addon, referenceDate, durationId, options = {}) {
  const validRules = getValidPricingRules(addon?._pricingRules || [], referenceDate, options);
  if (validRules.length === 0) return null;
  return findMatchingPricingRule(validRules, durationId, { cheapestAmongEqual: true });
}

/**
 * Convert a pricing rule's duration to hours.
 *
 * @param {Object} rule - A pricing rule object with duration_type and duration_value
 * @returns {number} Duration in hours
 */
export function pricingRuleDurationToHours(rule) {
  const value = parseFloat(rule.duration_value);
  switch (rule.duration_type) {
    case 'hours':
      return value;
    case 'calendar_day':
    case 'multi_day':
      return value * 24;
    case 'week':
      return value * 24 * 7;
    default:
      return 0;
  }
}

/**
 * Find the next-larger pricing rule that covers a given actual duration.
 *
 * Rules are sorted ascending by duration in hours. The first rule whose
 * duration is >= actualDurationHours is returned. If no rule is large enough,
 * returns null (caller should show "maximum duration exceeded" message).
 *
 * @param {Array} pricingRules - Array of valid/active pricing rule objects
 * @param {number} actualDurationHours - The actual rental duration in hours
 * @returns {Object|null} The matching pricing rule, or null if none covers the duration
 */
export function findNextLargerPricingRule(pricingRules, actualDurationHours) {
  if (!pricingRules || pricingRules.length === 0) return null;

  // Sort ascending by duration in hours
  const sorted = [...pricingRules]
    .map(rule => ({ rule, hours: pricingRuleDurationToHours(rule) }))
    .filter(({ hours }) => hours > 0)
    .sort((a, b) => a.hours - b.hours);

  // Find first rule >= actualDurationHours
  const match = sorted.find(({ hours }) => hours >= actualDurationHours);
  return match ? match.rule : null;
}

/**
 * Get the maximum rental duration in hours from a set of pricing rules.
 *
 * @param {Array} pricingRules - Array of valid/active pricing rule objects
 * @returns {number} Maximum duration in hours, or 0 if no rules
 */
export function getMaxDurationHours(pricingRules) {
  if (!pricingRules || pricingRules.length === 0) return 0;

  return Math.max(
    ...pricingRules.map(rule => pricingRuleDurationToHours(rule)).filter(h => h > 0),
    0
  );
}

/**
 * Convert a pricing rule to a duration object compatible with bookingData.duration.
 *
 * @param {Object} rule - A pricing rule object
 * @returns {{ id: string, label: string|null, labelTranslations: Array|undefined, durationType: string|undefined }} Duration object
 */
export function pricingRuleToDuration(rule) {
  const value = Math.floor(parseFloat(rule.duration_value));

  let id;
  switch (rule.duration_type) {
    case 'hours':
      id = `${value}h`;
      break;
    case 'calendar_day':
    case 'multi_day':
      if (value === 7) {
        id = '1w';
      } else if (value === 14) {
        id = '2w';
      } else {
        id = `${value}d`;
      }
      break;
    case 'week':
      id = `${value}w`;
      break;
    default:
      id = `${value}h`;
  }

  return {
    id,
    label: rule.label || null,
    labelTranslations: rule.label_translations,
    durationType: rule.duration_type,
  };
}
